import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';
import { 
  Mail, 
  Download, 
  Trash2, 
  Users, 
  TrendingUp, 
  Calendar,
  Search,
  Eye,
  EyeOff,
  Filter,
  RefreshCw
} from 'lucide-react';

const AdminNewsletter = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [showDetails, setShowDetails] = useState({});
  
  const { adminAuthToken } = useAdminAuth();
  const { toast } = useToast();

  // Fetch newsletter subscriptions
  const fetchSubscriptions = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscriptions?active_only=${activeOnly}&limit=100`, 
        {
          headers: {
            'Authorization': `Bearer ${adminAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      } else {
        throw new Error('Errore nel caricamento delle iscrizioni');
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le iscrizioni alla newsletter",
        variant: "destructive"
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Fetch newsletter statistics
  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/stats`,
        {
          headers: {
            'Authorization': `Bearer ${adminAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Unsubscribe user
  const handleUnsubscribe = async (email, subscriptionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/unsubscribe`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            subscription_id: subscriptionId
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Successo",
          description: "Utente disiscritto con successo"
        });
        fetchSubscriptions(false);
        fetchStats();
      } else {
        toast({
          title: "Errore",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error unsubscribing user:', error);
      toast({
        title: "Errore",
        description: "Errore durante la disiscrizione",
        variant: "destructive"
      });
    }
  };

  // Export subscriptions to CSV
  const handleExport = () => {
    try {
      const csvContent = [
        'Email,Nome,Data Iscrizione,Attivo,Source',
        ...filteredSubscriptions.map(sub => 
          `${sub.email},"${sub.name || ''}","${new Date(sub.subscribed_at || sub.created_at).toLocaleDateString()}",${sub.is_active ? 'Sì' : 'No'},"${sub.source || ''}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `newsletter_subscriptions_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast({
        title: "Export completato",
        description: `Esportate ${filteredSubscriptions.length} iscrizioni`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Errore Export",
        description: "Errore durante l'esportazione",
        variant: "destructive"
      });
    }
  };

  // Toggle subscription details
  const toggleDetails = (subscriptionId) => {
    setShowDetails(prev => ({
      ...prev,
      [subscriptionId]: !prev[subscriptionId]
    }));
  };

  // Filter subscriptions based on search term
  const filteredSubscriptions = subscriptions.filter(sub => {
    const searchLower = searchTerm.toLowerCase();
    return (
      sub.email?.toLowerCase().includes(searchLower) ||
      sub.name?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
  }, [activeOnly]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Newsletter</h1>
          <p className="text-gray-600">Gestisci le iscrizioni alla newsletter</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => fetchSubscriptions()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Totale Iscritti</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total_subscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Mail className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Attivi</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.active_subscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <EyeOff className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Disiscritti</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.inactive_subscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Ultimi 30gg</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.recent_subscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Cerca per email o nome..."
              />
            </div>

            {/* Active Filter */}
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Solo attivi</span>
              </label>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-end">
              <span className="text-sm text-gray-700">
                {filteredSubscriptions.length} risultati
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredSubscriptions.length === 0 ? (
            <li className="p-6 text-center text-gray-500">
              <Mail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna iscrizione</h3>
              <p className="mt-1 text-sm text-gray-500">
                Non ci sono iscrizioni che corrispondono ai tuoi filtri.
              </p>
            </li>
          ) : (
            filteredSubscriptions.map((subscription) => (
              <li key={subscription.subscription_id} className="hover:bg-gray-50">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-3 h-3 rounded-full ${subscription.is_active ? 'bg-green-400' : 'bg-red-400'}`}></div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">{subscription.email}</p>
                          {subscription.name && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {subscription.name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>
                            <Calendar className="w-3 h-3 mr-1 inline" />
                            {new Date(subscription.subscribed_at || subscription.created_at).toLocaleDateString('it-IT')}
                          </span>
                          <span>Fonte: {subscription.source || 'N/A'}</span>
                          <span className={subscription.is_active ? 'text-green-600' : 'text-red-600'}>
                            {subscription.is_active ? 'Attivo' : 'Disiscritto'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleDetails(subscription.subscription_id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {showDetails[subscription.subscription_id] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      {subscription.is_active && (
                        <button
                          onClick={() => handleUnsubscribe(subscription.email, subscription.subscription_id)}
                          className="text-red-400 hover:text-red-600"
                          title="Disiscrivere"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {showDetails[subscription.subscription_id] && (
                    <div className="mt-4 pl-7 border-l-2 border-gray-200">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">ID Iscrizione</dt>
                          <dd className="text-sm text-gray-900 font-mono">{subscription.subscription_id}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Data Iscrizione</dt>
                          <dd className="text-sm text-gray-900">
                            {new Date(subscription.subscribed_at || subscription.created_at).toLocaleString('it-IT')}
                          </dd>
                        </div>
                        {subscription.unsubscribed_at && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Data Disiscrizione</dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(subscription.unsubscribed_at).toLocaleString('it-IT')}
                            </dd>
                          </div>
                        )}
                        {subscription.preferences && Object.keys(subscription.preferences).length > 0 && (
                          <div className="sm:col-span-2">
                            <dt className="text-xs font-medium text-gray-500">Preferenze</dt>
                            <dd className="text-sm text-gray-900">
                              <pre className="text-xs bg-gray-100 p-2 rounded">
                                {JSON.stringify(subscription.preferences, null, 2)}
                              </pre>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;